import dayjs from 'dayjs';

import type { PillColor } from 'components/pill/Pill';
import type {
  UserActivityTimelineItem,
  UserActivityUserDetail,
} from 'src/app/audit-log/endpoints/UserActivityStatsEndpoint.types';
import { AuditLogActionType } from 'src/types/enums';

export const ACTION_TYPE_FILTERS = [
  AuditLogActionType.Created,
  AuditLogActionType.Updated,
  AuditLogActionType.Deleted,
  AuditLogActionType.Read,
] as const;

export type ActionTypeFilter = (typeof ACTION_TYPE_FILTERS)[number];
export type ActionTypeFilterOption = 'all' | ActionTypeFilter;

export function filterTimelineByActionType(
  timeline: UserActivityTimelineItem[],
  actionTypeFilter: ActionTypeFilterOption,
): UserActivityTimelineItem[] {
  if (actionTypeFilter === 'all') {
    return timeline;
  }

  return timeline.filter((entry) => entry.actionType === actionTypeFilter);
}

export function filterUserDetail(
  user: UserActivityUserDetail,
  actionTypeFilter: ActionTypeFilterOption,
): UserActivityUserDetail {
  const timeline = filterTimelineByActionType(user.timeline, actionTypeFilter);
  const actionCountsByType =
    actionTypeFilter === 'all'
      ? user.actionCountsByType
      : user.actionCountsByType.filter((item) => item.actionType === actionTypeFilter);

  const logins = timeline.filter((entry) => entry.description.toLowerCase().includes('logged in'));
  const loginCount = logins.length;
  const lastLoginAt = logins.length > 0 ? logins[0].createdAt : null;
  const lastActivityAt = timeline.length > 0 ? timeline[0].createdAt : null;

  return {
    ...user,
    timeline,
    actionCountsByType,
    logins: logins.map((entry) => ({
      createdAt: entry.createdAt,
      description: entry.description,
    })),
    loginCount,
    lastLoginAt,
    lastActivityAt,
    totalActions: timeline.length,
    activePeriods: computeActivePeriodsFromTimeline(timeline),
  };
}

export function groupTimelineByDate(
  timeline: UserActivityTimelineItem[],
): { date: string; entries: UserActivityTimelineItem[] }[] {
  const groups = new Map<string, UserActivityTimelineItem[]>();

  for (const entry of timeline) {
    const date = dayjs(entry.createdAt).format('YYYY-MM-DD');
    const existing = groups.get(date) ?? [];
    existing.push(entry);
    groups.set(date, existing);
  }

  return [...groups.entries()].sort(([a], [b]) => b.localeCompare(a)).map(([date, entries]) => ({ date, entries }));
}

export function formatPeriodDuration(startAt: string, endAt: string): string {
  const minutes = Math.max(1, Math.round((new Date(endAt).getTime() - new Date(startAt).getTime()) / 60_000));
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}u ${remainingMinutes}m` : `${hours}u`;
}

export function getActionPillColor(actionType: string): PillColor {
  switch (actionType) {
    case AuditLogActionType.Created:
      return 'positive';
    case AuditLogActionType.Updated:
      return 'primary';
    case AuditLogActionType.Deleted:
      return 'negative';
    case AuditLogActionType.Read:
      return 'default';
    default:
      return 'muted';
  }
}

const ACTIVE_PERIOD_GAP_MINUTES = 30;

function computeActivePeriodsFromTimeline(
  timeline: UserActivityTimelineItem[],
): UserActivityUserDetail['activePeriods'] {
  if (timeline.length === 0) {
    return [];
  }

  const sorted = [...timeline].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  const gapMs = ACTIVE_PERIOD_GAP_MINUTES * 60_000;
  const periods: UserActivityUserDetail['activePeriods'] = [];

  let startAt = sorted[0].createdAt;
  let endAt = sorted[0].createdAt;
  let actionCount = 1;

  for (let i = 1; i < sorted.length; i++) {
    const entry = sorted[i];
    const gap = new Date(entry.createdAt).getTime() - new Date(endAt).getTime();

    if (gap <= gapMs) {
      endAt = entry.createdAt;
      actionCount++;
      continue;
    }

    periods.push({ startAt, endAt, actionCount });
    startAt = entry.createdAt;
    endAt = entry.createdAt;
    actionCount = 1;
  }

  periods.push({ startAt, endAt, actionCount });
  return periods;
}
